import LoadingOverlayDots from "@/components/hlidaci-pes/LoadingLayoverDots";
import { PortfolioSelectorButton } from "@/components/portfolio/portfolio-page/PortfolioSelectorButton";
import { Button } from "@/components/ui/Button";
import { SummaryStatGrid } from "@/components/ui/SummaryGrid";
import { OverviewSurface } from "@/components/ui/OverviewSurface";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import type { ClientEstate, Transaction } from "@/types";
import { expandRecurringTransactions, formatCZK } from "@/utils/MyEstateHelper";
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import type { ReactNode } from "react";

type PortfolioEstateSelectionPanelProps = {
  estates: ClientEstate[];
  estateCountLabel: string;
  isLoading: boolean;
  selectedEstateId: number | null;
  currentEstateValue: number;
  portfolioEstimatedValue: number | null;
  embedded?: boolean;
  onSelectEstate: (estate: ClientEstate | null) => void;
  onCreateEstate: () => void;
  createActionLabel?: string;
  emptyContent?: ReactNode;
  headerAction?: ReactNode;
};

type ChartPoint = { x: number; y: number };

type TrendData = {
  points: ChartPoint[];
  total: number;
};

type MonthlyConfirmationSummary = {
  confirmed: number;
  confirmedAmount: number;
  missing: number;
  total: number;
};

function formatCompactCZK(value: number, locale: string) {
  if (value <= 0) return "—";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "CZK",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

function getTransactionValue(transaction: Transaction) {
  return transaction.transactionType === "INCOME"
    ? transaction.amount
    : -transaction.amount;
}

function getYearProfit(estates: ClientEstate[], year: number, today: Date) {
  return estates
    .flatMap((estate) =>
      expandRecurringTransactions(estate.transactions ?? [], year, today),
    )
    .filter(
      (transaction) =>
        transaction.showInDashboard &&
        new Date(transaction.dateFrom).getFullYear() === year,
    )
    .reduce(
      (total, transaction) => total + getTransactionValue(transaction),
      0,
    );
}

function getVerificationPeriod(date: string) {
  const transactionDate = new Date(date);
  return `${String(transactionDate.getMonth() + 1).padStart(2, "0")} ${transactionDate.getFullYear()}`;
}

function getMonthlyConfirmationSummary(
  estates: ClientEstate[],
  today: Date,
): MonthlyConfirmationSummary {
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const confirmableTransactions = estates
    .flatMap((estate) =>
      expandRecurringTransactions(
        estate.transactions ?? [],
        currentYear,
        today,
      ),
    )
    .filter((transaction) => {
      const transactionDate = new Date(transaction.dateFrom);
      return (
        transaction.transactionType === "INCOME" &&
        !transaction.isPrediction &&
        transactionDate.getFullYear() === currentYear &&
        transactionDate.getMonth() === currentMonth
      );
    });

  const confirmedTransactions = confirmableTransactions.filter((transaction) =>
    transaction.verification?.listOfVerifiedTransactions?.includes(
      getVerificationPeriod(transaction.dateFrom),
    ),
  );

  return {
    confirmed: confirmedTransactions.length,
    confirmedAmount: confirmedTransactions.reduce(
      (total, transaction) => total + transaction.amount,
      0,
    ),
    missing: confirmableTransactions.length - confirmedTransactions.length,
    total: confirmableTransactions.length,
  };
}

function getTrendData(estates: ClientEstate[], today: Date): TrendData {
  const currentYear = today.getFullYear();
  const transactions = estates
    .flatMap((estate) =>
      expandRecurringTransactions(
        estate.transactions ?? [],
        currentYear,
        today,
      ),
    )
    .filter(
      (transaction) =>
        transaction.showInDashboard &&
        !transaction.isPrediction &&
        new Date(transaction.dateFrom).getFullYear() === currentYear,
    )
    .sort(
      (first, second) =>
        new Date(first.dateFrom).getTime() -
        new Date(second.dateFrom).getTime(),
    );

  if (transactions.length === 0) {
    return {
      points: [
        { x: 0, y: 66 },
        { x: 80, y: 66 },
        { x: 160, y: 66 },
        { x: 240, y: 66 },
      ],
      total: 0,
    };
  }

  const values = [0];
  let runningTotal = 0;
  transactions.forEach((transaction) => {
    runningTotal += getTransactionValue(transaction);
    values.push(runningTotal);
  });

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1);

  return {
    points: values.map((value, index) => ({
      x: (index / Math.max(values.length - 1, 1)) * 240,
      y: 66 - ((value - min) / range) * 52,
    })),
    total: runningTotal,
  };
}

function getSmoothPath(points: ChartPoint[]) {
  if (points.length === 0) return "";

  return points.slice(1).reduce((path, point, index) => {
    const previous = points[index];
    const middleX = (previous.x + point.x) / 2;
    return `${path} C ${middleX} ${previous.y}, ${middleX} ${point.y}, ${point.x} ${point.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
}

export function PortfolioEstateSelectionPanel({
  estates,
  estateCountLabel,
  isLoading,
  selectedEstateId,
  currentEstateValue,
  portfolioEstimatedValue,
  embedded = false,
  onSelectEstate,
  onCreateEstate,
  createActionLabel,
  emptyContent,
  headerAction,
}: PortfolioEstateSelectionPanelProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      portfolio: "Moje portfolio",
      totalValue: "Celková hodnota",
      yearOverYearChange: "Příjem za 12 měsíců",
      selected: "Vybráno",
      value: "Odhadovaná hodnota",
      allProperties: "Všechny nemovitosti",
      addProperty: "Přidat nemovitost",
      trendLabel: "Skutečný vývoj cash flow portfolia v aktuálním roce",
      confirmedCount: (confirmed: number, total: number) =>
        `${confirmed} z ${total} potvrzeno`,
    },
    en: {
      portfolio: "My portfolio",
      totalValue: "Total value",
      yearOverYearChange: "Income over 12 months",
      selected: "Selected",
      value: "Estimated value",
      allProperties: "All properties",
      addProperty: "Add property",
      trendLabel: "Actual portfolio cash flow trend for the current year",
      confirmedCount: (confirmed: number, total: number) =>
        `${confirmed} of ${total} confirmed`,
    },
  }[language];
  const locale = language === "cs" ? "cs-CZ" : "en-US";
  const selectedEstate =
    estates.find((estate) => estate.id === selectedEstateId) ?? null;
  const selectedPurchasePrice = selectedEstate?.purchasePrice ?? 0;
  const hasCurrentEstateValue =
    selectedEstate != null && currentEstateValue > 0;
  const displayedValue = selectedEstate
    ? hasCurrentEstateValue
      ? currentEstateValue
      : selectedPurchasePrice
    : (portfolioEstimatedValue ?? 0);
  const trendEstates = selectedEstate ? [selectedEstate] : estates;
  const today = new Date();
  const formattedCurrentMonth = new Intl.DateTimeFormat(locale, {
    month: "long",
  }).format(today);
  const currentMonthLabel = `${formattedCurrentMonth.charAt(0).toLocaleUpperCase(locale)}${formattedCurrentMonth.slice(1)}`;
  const currentYear = today.getFullYear();
  const currentTransactionProfit = getYearProfit(
    trendEstates,
    currentYear,
    today,
  );
  const lastYearTransactionProfit = getYearProfit(
    trendEstates,
    currentYear - 1,
    today,
  );
  const yearOverYearDifference =
    currentTransactionProfit - lastYearTransactionProfit;
  const displayedReturn =
    lastYearTransactionProfit !== 0
      ? (yearOverYearDifference / lastYearTransactionProfit) * 100
      : 0;
  const isYearOverYearUp = yearOverYearDifference >= 0;
  const trendData = getTrendData(trendEstates, today);
  const trendPath = getSmoothPath(trendData.points);
  const areaPath = `${trendPath} L 240 80 L 0 80 Z`;
  const isTrendNegative = trendData.total < 0;
  const confirmationSummary = getMonthlyConfirmationSummary(
    trendEstates,
    today,
  );
  const confirmationValue = copy.confirmedCount(
    confirmationSummary.confirmed,
    confirmationSummary.total,
  );
  const summaryStats = [
    {
      label: copy.selected,
      value: selectedEstate?.name ?? copy.allProperties,
    },
    {
      label: copy.value,
      value: displayedValue > 0 ? formatCZK(displayedValue) : "—",
    },
    {
      label: copy.yearOverYearChange,
      value: `${displayedReturn >= 0 ? "+" : ""}${displayedReturn.toLocaleString(
        locale,
        { maximumFractionDigits: 1 },
      )} %`,
      tone: isYearOverYearUp ? ("positive" as const) : ("negative" as const),
    },
    {
      label: currentMonthLabel,
      value: (
        <span className="flex flex-col gap-1">
          <span>{confirmationValue}</span>
          <span className="app-section-meta">
            {formatCZK(confirmationSummary.confirmedAmount)}
          </span>
        </span>
      ),
      tone:
        confirmationSummary.missing > 0
          ? ("warning" as const)
          : ("positive" as const),
      title: confirmationValue,
    },
  ];

  return (
    <OverviewSurface
      frame={embedded ? "none" : "card"}
      grow
      className="relative"
    >
      {headerAction ? (
        <div className="absolute right-0 top-0 z-20">{headerAction}</div>
      ) : null}
      <div
        className={cn(
          "flex items-center justify-between border-b border-softiq-primary/10 py-4 pl-5 dark:border-white/10 sm:pl-6",
          headerAction ? "pr-14" : "pr-5 sm:pr-6",
        )}
      >
        <div>
          <p className="app-section-eyebrow">{copy.portfolio}</p>
          <p className="app-section-heading mt-1">{estateCountLabel}</p>
        </div>
        <div className="inline-flex shrink-0 flex-col items-center gap-1">
          <Button
            type="button"
            variant="unstyled"
            onClick={onCreateEstate}
            aria-label={createActionLabel ?? copy.addProperty}
            title={createActionLabel ?? copy.addProperty}
            className="app-section-add-button group"
          >
            <BuildingOffice2Icon aria-hidden="true" className="!size-5" />
            <span className="app-section-add-badge">
              <PlusIcon
                aria-hidden="true"
                className="!size-2.5 stroke-[2.5] transition-transform group-hover:rotate-90"
              />
            </span>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="relative min-h-[240px] flex-1">
          <LoadingOverlayDots />
        </div>
      ) : (
        <div className="grid flex-1 lg:grid-cols-[0.9fr_1.1fr]">
          <button
            type="button"
            onClick={() => onSelectEstate(null)}
            aria-pressed={selectedEstateId == null}
            className="group flex min-h-[240px] flex-col border-b border-softiq-primary/10 p-5 text-left transition-colors hover:bg-white/35 dark:border-white/10 dark:hover:bg-white/[0.03] sm:p-6 lg:border-b-0 lg:border-r"
          >
            <span className="app-section-description">{copy.value}</span>
            <span className="mt-2 text-[2rem] font-medium leading-none tracking-[-0.05em]">
              {formatCompactCZK(displayedValue, locale)}
            </span>
            <span className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium dark:bg-white/10 ${
                  isYearOverYearUp
                    ? "bg-softiq-secondary-shade-200 text-softiq-primary dark:text-softiq-secondary"
                    : "bg-red-100 text-red-600 dark:text-red-400"
                }`}
              >
                {isYearOverYearUp ? (
                  <ArrowTrendingUpIcon aria-hidden="true" className="size-3" />
                ) : (
                  <ArrowTrendingDownIcon
                    aria-hidden="true"
                    className="size-3"
                  />
                )}
                {`${displayedReturn >= 0 ? "+" : ""}${displayedReturn.toLocaleString(
                  locale,
                  {
                    maximumFractionDigits: 1,
                  },
                )} %`}
              </span>
              <span className="app-meta-muted">{copy.yearOverYearChange}</span>
            </span>

            <svg
              className={`mt-5 h-20 w-full overflow-visible ${
                isTrendNegative
                  ? "text-red-600 dark:text-red-400"
                  : "text-softiq-primary dark:text-softiq-secondary"
              }`}
              viewBox="0 0 240 80"
              role="img"
              aria-label={copy.trendLabel}
              preserveAspectRatio="none"
            >
              <path d={areaPath} fill="url(#authenticated-portfolio-fill)" />
              <path
                d={trendPath}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient
                  id="authenticated-portfolio-fill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop stopColor="currentColor" stopOpacity="0.32" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </button>

          <div
            className={cn(
              "p-3 sm:p-4",
              estates.length > 0 || !emptyContent
                ? "max-h-[240px] overflow-y-auto"
                : "overflow-visible",
            )}
          >
            {estates.length > 0 ? (
              <div className="space-y-1">
                {estates.map((estate) => (
                  <PortfolioSelectorButton
                    key={estate.id}
                    estate={estate}
                    isActive={selectedEstateId === estate.id}
                    onClick={() =>
                      onSelectEstate(
                        selectedEstateId === estate.id ? null : estate,
                      )
                    }
                  />
                ))}
              </div>
            ) : emptyContent ? (
              emptyContent
            ) : (
              <button
                type="button"
                onClick={onCreateEstate}
                className="app-section-empty"
              >
                {copy.addProperty}
              </button>
            )}
          </div>
        </div>
      )}

      {!isLoading ? <SummaryStatGrid stats={summaryStats} /> : null}
    </OverviewSurface>
  );
}
